/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import css from '../../css/loan-escrow-accruals-styles/MortgageInsAccruals.module.css';
import {
  phoneNumberFormatter,
  epochToDate,
  formatDate,
  epochToAnniMonth,
  addMonthsToDate,
} from '../../common/UtilityFunctions';
import { ImpoundPolicyListResponse, InsCompResponse } from '../../common/queryFunctions';
import * as constants from '../../common/constants';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { useAuthContext } from '../../contexts/AuthContext';
import useInsCompInfo from '../../common/hooks/useInsCompInfo';

interface ComponentProps {
  miImpounds: Array<ImpoundPolicyListResponse>;
  upfrontMIP: number;
  upfrontPMI: number;
  miCompany: string;
  miCertificate: string;
  fhaCaseNo: string;
  firstPaymentDate: Record<string, number>;
  closingDate: number;
  amortTerm: number;
  ltv: number;
  compCodes: Set<string>;
  miMonthsReq: Record<string, number>;
}
const MortgageInsAccruals: React.FC<ComponentProps> = (props) => {
  const { token } = useAuthContext();
  const [showPaidImpoundLines, setShowPaidImpoundLines] = useState<boolean>(false);
  const [mortgageInsImpounds, setMortgageInsImpounds] = useState<{ [key: string]: ImpoundPolicyListResponse[] }>({});
  const [halfDecades, setHalfDecades] = useState<Array<number>>([]);
  const [selectedHalfDecade, setSelectedHalfDecade] = useState<string>('');

  const {
    data: miPayeeInfo,
    status: miPayeeStatus,
    error: miPayeeErrors,
  } = useInsCompInfo(props.compCodes, token, 'miInsCompInfo');

  const flattenedMiImpounds: Array<ImpoundPolicyListResponse> = useMemo(() => {
    let tempArray: Array<ImpoundPolicyListResponse> = props.miImpounds.flatMap((impList) => impList);
    tempArray = tempArray.filter(
      (impound) =>
        impound.impoundType === 'MI01' ||
        impound.impoundType === 'MI02' ||
        impound.impoundType === 'MI03' ||
        impound.impoundType === 'MI09',
    );
    tempArray.sort((a, b) => {
      //if impound types are the same, order by payment due date
      return a.renewalDte - b.renewalDte;
    });
    return tempArray;
  }, [props.miImpounds]);

  //this effect sorts mortgage insurance accruals by payments status and hald decade period of due date
  useEffect(() => {
    const indexes: { [key: string]: ImpoundPolicyListResponse[] } = { ['unpaid']: [] };
    let currentHalfDecade: number = 0;
    let currentHalfDecadeFirstIndex: number = 0;
    for (let i = 0; i < flattenedMiImpounds.length; i++) {
      const currentMortgageInsImpound: ImpoundPolicyListResponse = flattenedMiImpounds[i];
      const year: number = epochToDate(currentMortgageInsImpound.renewalDte).getFullYear();
      const halfDecade: number = Math.floor(year / 5) * 5;
      if (currentHalfDecade === 0) {
        currentHalfDecade = halfDecade;
      }
      if (halfDecade !== currentHalfDecade) {
        indexes[currentHalfDecade.toString()] = flattenedMiImpounds.slice(currentHalfDecadeFirstIndex, i);
        if (!halfDecades.includes(currentHalfDecade)) {
          setHalfDecades([...halfDecades, currentHalfDecade]);
        }
        currentHalfDecade = halfDecade;
        currentHalfDecadeFirstIndex = i;
      }
      if (currentMortgageInsImpound.servPremium > currentMortgageInsImpound.servAmtPaid) {
        indexes['unpaid'] = [...indexes['unpaid'], currentMortgageInsImpound];
      }
    }
    if (flattenedMiImpounds.length !== 0) {
      indexes[currentHalfDecade.toString()] = flattenedMiImpounds.slice(currentHalfDecadeFirstIndex);
      if (!halfDecades.includes(currentHalfDecade)) {
        setHalfDecades([...halfDecades, currentHalfDecade]);
      }
    }
    setMortgageInsImpounds(indexes);
  }, [flattenedMiImpounds, halfDecades]);

  //sets the selected half decade to the first half-decade upon population of the half-decade array
  useEffect(() => {
    if (halfDecades.length > 0) {
      halfDecades.sort((a, b) => a - b);
      setSelectedHalfDecade(halfDecades[0].toString());
    }
  }, [halfDecades, showPaidImpoundLines]);

  const handlePaidImpoundToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setShowPaidImpoundLines(value);
  }, []);

  const handleHalfDecadeSelection = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value;
    setSelectedHalfDecade(value);
  }, []);

  if (miPayeeStatus === 'pending') return <div>Loading...</div>;
  if (miPayeeErrors.length > 0)
    return (
      <>
        <h2>Encountered errors while obtaining Mortgage Insurance Information.</h2>
        <ul>
          {miPayeeErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
        </ul>
      </>
    );

  return (
    <>
      <form>
        <span>Show Fully Paid Impound Lines?</span>
        <input
          type='checkbox'
          name='showPaidImpoundLines'
          checked={showPaidImpoundLines}
          onChange={handlePaidImpoundToggle}
        ></input>
        {showPaidImpoundLines && halfDecades.length > 0 && (
          <div>
            <span>Select Half-Decade:</span>
            {halfDecades.map((halfDecade, index) => (
              <label key={index} className={css.radioLabel}>
                <input
                  type='radio'
                  value={halfDecade.toString()}
                  checked={selectedHalfDecade === halfDecade.toString()}
                  onChange={handleHalfDecadeSelection}
                ></input>
                {`${halfDecade.toString()} - ${(halfDecade + 4).toString()}`}
              </label>
            ))}
          </div>
        )}
      </form>
      <table className={css.miTable}>
        <caption className='h3 semibold'>Mortgage&nbsp;Insurance&nbsp;Accruals</caption>
        <thead>
          <tr>
            <th>Impound Type</th>
            <th>Payee</th>
            <th>Due Date</th>
            <th>Payment Due</th>
            <th>Payment Status</th>
            <th>Policy No.</th>
            <th>Escrowed?</th>
            <th>Anniversary Month</th>
            <th>Financed Premium</th>
            <th>Anticipated Cancelation Date</th>
            <th>Actual Cancelation Date</th>
            <th>Comment</th>
          </tr>
        </thead>
        <tbody>
          {Object.values(mortgageInsImpounds).some((impounds) => Array.isArray(impounds) && impounds.length > 0) &&
            mortgageInsImpounds[!showPaidImpoundLines ? 'unpaid' : selectedHalfDecade].map((impound, index) => {
              const payeeInfo: InsCompResponse =
                impound.impoundType === 'MI01'
                  ? miPayeeInfo.find((miPayee) => miPayee.compCode === impound.insuranceComp.replace(/-/g, ''))
                  : impound.impoundType === 'MI02' || impound.impoundType === 'MI03'
                    ? miPayeeInfo.find((miPayee) => miPayee.compCode === constants.hudCompCode)
                    : impound.impoundType === 'MI09'
                      ? miPayeeInfo.find((miPayee) => miPayee.compCode === constants.usdaCompCode)
                      : null;

              const paymentStatus: string =
                impound.servPremium <= impound.servAmtPaid
                  ? 'FULLY PAID'
                  : impound.servAmtPaid > 0
                    ? 'PARTLY PAID'
                    : 'NOT PAID';

              const upfrontPremium: number =
                impound.impoundType === 'MI01'
                  ? props.upfrontPMI
                  : impound.impoundType === 'MI02' || impound.impoundType === 'MI03' || impound.impoundType === 'MI09'
                    ? props.upfrontMIP
                    : 0;

              const firstPmntDate: number = props.firstPaymentDate
                ? props.firstPaymentDate[impound.impoundType]
                : undefined;
              const anniDate: string =
                (impound.impoundType === 'MI02' || impound.impoundType === 'MI03') && firstPmntDate
                  ? epochToAnniMonth(firstPmntDate)
                  : 'N/A';
              const monthsRequired: number = props.miMonthsReq ? props.miMonthsReq[impound.impoundType] : undefined;

              return (
                <tr key={index}>
                  <td>{impound.impoundType}</td>
                  {payeeInfo ? (
                    <td>
                      {payeeInfo.name1}{' '}
                      {phoneNumberFormatter(payeeInfo.primaryContactPhone, payeeInfo.primaryContactPhoneExtension)}
                    </td>
                  ) : (
                    <td></td>
                  )}
                  {impound.renewalDte ? <td>{formatDate(epochToDate(impound.renewalDte))}</td> : <td></td>}
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={impound.servPremium}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  <td>{paymentStatus}</td>
                  <td>{impound.policyNum}</td>
                  <td>{impound.escrowed ? 'Yes' : 'No'}</td>
                  <td>{anniDate}</td>
                  <td>
                    <NumericFormat
                      displayType='text'
                      value={upfrontPremium}
                      decimalScale={2}
                      fixedDecimalScale
                      prefix='$'
                      thousandSeparator
                    />
                  </td>
                  {/*expected cancelation dates only apply for MI01/MI02 impound payments*/}
                  {(monthsRequired || monthsRequired === 0) &&
                  (impound.impoundType === 'MI01' || impound.impoundType === 'MI02') ? (
                    <td>{addMonthsToDate(firstPmntDate, monthsRequired)}</td>
                  ) : (
                    <td>{!['MI01', 'MI02'].includes(impound.impoundType) && 'N/A'}</td>
                  )}
                  {impound.cancelDte ? <td>{formatDate(epochToDate(impound.cancelDte))}</td> : <td></td>}
                  <td>{impound.comment}</td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </>
  );
};
export default MortgageInsAccruals;
